/**
 * `?next=` deep links (outreach, ads, or optional future external sites).
 * Only allow-listed keys are accepted (never raw paths), so this cannot become an open redirect.
 * The key is persisted in localStorage so it survives signup → email verification (possibly in a
 * new tab) → onboarding → first landing page.
 */
export const NEXT_TARGETS = {
  'website-review': '/app/review',
  'quote-requests': '/app/quote-requests',
  'reviews-proof': '/app/reviews-proof',
  'social-early-access': '/app/social',
} as const;

export type NextKey = keyof typeof NEXT_TARGETS;

const STORAGE_KEY = 'ttp_next';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function parseNextKey(value: string | null | undefined): NextKey | null {
  const v = value?.trim().toLowerCase();
  if (v && Object.prototype.hasOwnProperty.call(NEXT_TARGETS, v)) return v as NextKey;
  return null;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Read ?next= from the URL and remember it. Returns the key captured (if any). */
export function captureNextParam(search: string = window.location.search): NextKey | null {
  const key = parseNextKey(new URLSearchParams(search).get('next'));
  if (!key) return null;
  storage()?.setItem(STORAGE_KEY, JSON.stringify({ key, at: Date.now() }));
  return key;
}

export function getStoredNextKey(): NextKey | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: string; at?: number };
    if (!parsed.at || Date.now() - parsed.at > TTL_MS) {
      s.removeItem(STORAGE_KEY);
      return null;
    }
    return parseNextKey(parsed.key);
  } catch {
    s.removeItem(STORAGE_KEY);
    return null;
  }
}

export function getNextPath(): string | null {
  const key = getStoredNextKey();
  return key ? NEXT_TARGETS[key] : null;
}

/** Return the deep-link path (or fallback) and clear the stored value. */
export function consumeNextPath(fallback = '/app'): string {
  const path = getNextPath();
  storage()?.removeItem(STORAGE_KEY);
  return path ?? fallback;
}

/** Current URL's ?next= (valid key only), else the stored one. */
export function currentNextKey(): NextKey | null {
  return parseNextKey(new URLSearchParams(window.location.search).get('next')) ?? getStoredNextKey();
}

/** Search params to carry between /signup and /login links. */
export function nextSearch(): { next?: NextKey } {
  const key = currentNextKey();
  return key ? { next: key } : {};
}
